import { useRef, useState } from 'react';
import { Paperclip, Upload } from 'lucide-react';
import { crm, uploadFiles, type FileRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { formatBytes } from '@/lib/utils';

interface Props {
  projectId: string;
  files: FileRow[];
  onChanged: () => void;
}

/** Am Projekt hängende Dateien, mit Upload direkt an das Projekt. */
export function FilesCard({ projectId, files, onChanged }: Props) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const hochladen = async (auswahl: FileList | null) => {
    if (!auswahl || auswahl.length === 0) return;
    setBusy(true);
    setFehler(null);
    try {
      const hochgeladen = await uploadFiles(Array.from(auswahl));
      await crm.projects.link(projectId, { fileIds: hochgeladen.map((f) => f.id) });
      onChanged();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Hochladen fehlgeschlagen');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Paperclip className="h-4 w-4 text-muted-foreground" />
          Dateien
        </h2>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => void hochladen(e.target.files)}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Datei hochladen"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-4 w-4" />
        </Button>
      </div>

      {files.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">Noch keine Datei am Projekt.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {files.map((f) => (
            <li key={f.id} className="text-sm">
              <a
                href={`/api/files/${f.id}`}
                className="block truncate font-medium hover:underline"
              >
                {f.name}
              </a>
              <span className="block text-xs text-muted-foreground">
                {formatBytes(f.sizeBytes)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {fehler && <p className="mt-2 text-xs text-danger">{fehler}</p>}
    </Card>
  );
}
